import React, { FC, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import cn from 'classnames';
import { motion } from 'framer-motion';
import { seconds2timestring } from 'common/utils/seconds2timestring';
import { Translate } from 'i18n';
import UrlIcon from 'students/views/shared/components/UrlIcon';
import { AudioRecorder } from 'common/modules/AudioRecorder';
import crossIcon from 'students/views/shared/assets/icons/close_icon.svg';
import { customMediaQuery, onLightTheme } from 'students/views/shared/styled';

import ShortEssayConfirmationModal from '../../../ShortEssayConfirmationModal';

interface IReplyAudioRecorder {
  handleRecordSend: (record: Blob | null) => void;
  minimumRecordingTime: number;
  isLoading: boolean;
  disabledSubmit?: boolean;
  onClose?: () => void;
  className?: string;
}

const ReplyAudioRecorder: FC<IReplyAudioRecorder> = ({
  handleRecordSend,
  minimumRecordingTime,
  isLoading,
  disabledSubmit,
  onClose,
  className
}) => {
  const [showSendButton, setShowSendButton] = useState<boolean>(false);
  const [recordedAudio, setRecordedAudio] = useState<Blob | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [currentDuration, setCurrentDuration] = useState('0');

  const handleOnRecordChange = useCallback(
    (record: Blob | null) => {
      if (record) {
        setRecordedAudio(record);
        setShowSendButton(true);
      }
    },
    [setRecordedAudio, setShowSendButton]
  );

  const handleOnRecordSend = useCallback(async () => {
    // Measure the recording duration to enforce the minimum-length gate. This
    // must never silently swallow the Send: on any failure (AudioContext cap
    // reached, undecodable/empty blob) fall back to sending the recording.
    let audioContext: AudioContext | null = null;
    try {
      const audioArrayBuffer = await recordedAudio?.arrayBuffer();
      if (!audioArrayBuffer) {
        handleRecordSend(recordedAudio);
        return;
      }

      audioContext = new AudioContext();
      audioContext.decodeAudioData(
        audioArrayBuffer,
        (buffer) => {
          setCurrentDuration(buffer.duration.toFixed(0));
          if (buffer.duration < minimumRecordingTime) {
            setShowModal(true);
          } else {
            handleRecordSend(recordedAudio);
          }
          audioContext?.close();
        },
        () => {
          // Decode failed — still send the recording rather than dropping it.
          handleRecordSend(recordedAudio);
          audioContext?.close();
        }
      );
    } catch {
      audioContext?.close();
      handleRecordSend(recordedAudio);
    }
  }, [handleRecordSend, recordedAudio, minimumRecordingTime]);

  const audioRecorderOptions = useMemo(
    () => ({ height: 60, barMinHeight: 1, normalize: true }),
    []
  );

  return (
    <SWrapper
      layout
      className={cn(className)}
      initial={{ opacity: 0, scaleY: 0 }}
      animate={{ opacity: 1, scaleY: 1 }}
      dir="ltr"
    >
      <AudioRecorder
        onRecordChange={handleOnRecordChange}
        customOptions={audioRecorderOptions}
        submitButton={
          showSendButton && (
            <SSendBtn disabled={disabledSubmit || isLoading} onClick={handleOnRecordSend}>
              <Translate str="frontend.lesson_task.tasks.check.assignment.send_to_review" />
            </SSendBtn>
          )
        }
      />

      {!showSendButton && (
        <SMinimumRecordingTime>
          <Translate
            str="frontend.lesson_task.tasks.check.assignment.minimal_recording_time"
            params={{
              minimalRecordingTime: seconds2timestring(minimumRecordingTime)
            }}
          />
        </SMinimumRecordingTime>
      )}

      {typeof onClose === 'function' ? (
        <SCloseBtn onClick={onClose}>
          <UrlIcon
            url={crossIcon}
            height="16px"
            width="16px"
            color="var(--heading-text-color)"
          />
        </SCloseBtn>
      ) : null}

      <ShortEssayConfirmationModal
        onSend={() => {
          handleRecordSend(recordedAudio);
        }}
        show={showModal}
        close={() => setShowModal(false)}
        message={
          <Translate
            str="frontend.lesson_task.tasks.check.assignment.recording_does_not_have_enough_duration"
            params={{
              minDurationNumber: `${minimumRecordingTime}s`,
              currentDurationNumber: `${currentDuration}s`
            }}
          />
        }
        continueBtnText={
          <Translate str="frontend.lesson_task.tasks.check.assignment.continue_recording" />
        }
      />
    </SWrapper>
  );
};

export default ReplyAudioRecorder;

const SWrapper = styled(motion.div)`
  transform-origin: bottom;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 300px;
  position: relative;
  background: rgba(240, 240, 243, 1);

  ${onLightTheme`
    background: #ffffff;
  `}
  border-radius: 14px;
  padding: 16px;

  ${customMediaQuery('tablet')} {
    min-height: 300px;
    padding: 24px 32px 24px 24px;
  }
`;

const SMinimumRecordingTime = styled.p`
  margin: 24px 0 0;
  padding: 0;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.125rem;
  color: var(--sub-text-color);
  opacity: 0.8;
`;

const SSendBtn = styled.button`
  background-color: var(--button-primary-bg);
  font-weight: 700;
  color: black;
  font-size: 1rem;
  line-height: 1rem;
  padding: 0 16px;
  height: 3rem;
  transition: background-color 0.3s;
  width: 100%;
  border-radius: 16px;
  width: max-content;
`;

const SCloseBtn = styled.button`
  border: none;
  background: transparent;
  position: absolute;
  top: 10px;
  right: 10px;
  opacity: 0.4;
  transition: opacity 0.3s;

  &:hover {
    opacity: 0.7;
  }
`;
